import React, { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import useAPI from '../../api';
import useTranslate from '../../Hooks/useTranslate';
import {
  Grid,
  Drawer,
  Typography,
  Paper,
  Collapse,
  useTheme,
} from '@mui/material';
import { userDataAtom } from '../../Atoms';
import IconLogo from '../../Components/Logos/IconLogo';
import FiltersBreadCrumbs from '../../Pages/Dashboard/FiltersBreadCrumbs';
import {
  CenteredFlexBox,
  FullSizeCenteredFlexBox,
  ColumnFlexBox,
} from '../FlexBox';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Fab from '../Navbar/Fab';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ThemeFab from '../Button/ThemeFab';
import StyledSwitch from '../Inputs/StyledSwitch';
import capitalize from '../../Helpers/StringsFormatting/capitalize';

const useStyles = () => ({
  header: {
    backgroundImage: 'linear-gradient(90.37deg, #1D5A9D 21.99%, #61B340 100%)',
    padding: '16px',
    boxShadow: '0 3px 6px 1px rgba(0, 0, 0, 0.21)',
    color: '#fff',
    width: '100%',
  },
  fab: {
    background: 'rgba(255, 255, 255, 0.14)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    color: '#fff',
  },
});

const FiltersNavbarWrapper = function FiltersNavbarWrapper({
  children,
  currentFilters,
  open,
  onToggle,
  switchLabel = '',
  onSwitchClick = undefined,
  switchValue = false,
  isOperator = false,
  setCurrentFilters = () => {},
  title = 'Report',
}) {
  const API = useAPI();
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const translate = useTranslate();
  const userData = useAtomValue(userDataAtom);

  const [delay, setDelay] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const handleSwitchClick = () => {
    if (onSwitchClick) onSwitchClick();
  };

  const handleToggleAutoRefresh = () => {
    setAutoRefresh((prev) => !prev);
  };

  useEffect(() => {
    if (isOperator) return;

    API.getSettingValue(
      'REPORT_REFRESH_INTERVAL',
      (res) => {
        setDelay(Number(res));
      },
      () => {
        setDelay(0);
      }
    );
  }, []);

  useEffect(() => {
    if (!delay || !autoRefresh) return;

    let timer = setTimeout(
      () => setCurrentFilters({ ...currentFilters, autoRefresh: true }),
      delay * 1000
    );

    return () => {
      clearTimeout(timer);
    };
  }, [autoRefresh, delay, currentFilters]);

  return (
    <ColumnFlexBox
      gap={open ? '16px' : '8px'}
      transition="all 0.3s"
      width="100%"
      padding="16px"
    >
      <Paper sx={classes.header}>
        <FullSizeCenteredFlexBox gap="24px" justifyContent="space-between">
          <CenteredFlexBox gap="16px" width="33%">
            <IconLogo viewBox="0 0 200 200" width="60px" height="100%" />
            <Typography variant="h4" color="inherit" sx={{ flexGrow: 1 }}>
              {translate(title)}
            </Typography>
          </CenteredFlexBox>
          <CenteredFlexBox gap="16px" width="33%" justifyContent="center">
            <Fab
              title="BACK_HOME"
              sx={classes.fab}
              onClick={() => {
                sessionStorage.setItem('currentSection', 'PRIORITIES');
                navigate('/priorities');
              }}
            >
              <HomeRoundedIcon sx={{ color: '#fff' }} />
            </Fab>
            <ThemeFab sx={classes.fab} iconColor="#fff" />
            <Fab
              title="HELP"
              sx={classes.fab}
              href="https://docs.databeasy.it/"
            >
              <HelpOutlineRoundedIcon sx={{ color: '#fff' }} />
            </Fab>
            <Fab
              title="FILTERS"
              onClick={onToggle}
              sx={{
                ...classes.fab,
                border: open ? '1px solid #fff' : undefined,
              }}
            >
              <FilterAltOutlinedIcon sx={{ color: '#fff' }} />
            </Fab>
          </CenteredFlexBox>
          <CenteredFlexBox gap="8px" width="33%" justifyContent="flex-end">
            <AccountCircleOutlinedIcon color="inherit" />
            <Typography variant="h6" color="inherit">
              {capitalize(`${userData?.name} ${userData?.surname}`)}
            </Typography>
          </CenteredFlexBox>
        </FullSizeCenteredFlexBox>
      </Paper>
      <Collapse in={open} timeout={300} collapsedSize="0px">
        <Paper sx={{ width: '100%', padding: '16px 24px' }}>
          <Typography variant="h6">{translate('FILTERS')}</Typography>
          {children}
        </Paper>
      </Collapse>
      <CenteredFlexBox gap="16px" justifyContent="flex-end">
        {!isOperator && (
          <StyledSwitch
            id="toggle-autoreload-report"
            label={translate('AUTO_REFRESH')}
            size="large"
            sx={{
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
              whiteSpace: 'nowrap',
            }}
            onChange={handleToggleAutoRefresh}
            checked={autoRefresh}
          />
        )}
        {switchLabel && onSwitchClick && (
          <StyledSwitch
            id="toggle-switch-report"
            label={switchLabel}
            size="large"
            sx={{
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
              whiteSpace: 'nowrap',
            }}
            onChange={handleSwitchClick}
            checked={switchValue}
          />
        )}
      </CenteredFlexBox>
    </ColumnFlexBox>
  );
};

export default FiltersNavbarWrapper;
